// Base User Profile (matches Pydantic UserProfileBase)
export interface UserProfileBase {
  occupation?: string | null;
  school_organization?: string | null;
  favorite_subject_and_reasoning?: string | null;
  study_habits_methods?: string | null;
  challenging_subjects_areas?: string | null;
  general_notes?: string | null;
  profile_settings?: any | null; // Corresponds to JSONB
}

// User Profile (matches Pydantic UserProfile for API responses)
export interface UserProfile extends UserProfileBase {
  user_id: number;
}

// User Profile Update (matches Pydantic UserProfileUpdate)
export interface UserProfileUpdate extends UserProfileBase {}

// Base User (matches Pydantic UserBase)
export interface UserBase {
  username: string;
  email: string; // Pydantic EmailStr is string in TS
  first_name?: string | null;
  last_name?: string | null;
  birthday?: string | null; // Date as string (YYYY-MM-DD)
}

// User Create (matches Pydantic UserCreate)
export interface UserCreate extends UserBase {
  password: string;
  profile?: UserProfileBase | null; // Profile data for creation
}

// User Update (matches Pydantic UserUpdate)
export interface UserUpdate {
  username?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  birthday?: string | null;
  password?: string | null;
  profile?: UserProfileUpdate | null;
}

// User (matches Pydantic User for API responses)
export interface User extends UserBase {
  user_id: number;
  created_at: string; // TIMESTAMPTZ as string (ISO 8601)
  updated_at: string;
  last_login_at?: string | null;
  last_activity_at?: string | null;
  profile?: UserProfile | null;
  is_active?: boolean; // Assuming these might be added based on deps.py comments
  is_superuser?: boolean;
  profile_exists?: boolean; // Indicates whether user has completed profile
}

// Course Status Enum (matches SQLAlchemy Enum)
export enum CourseStatusEnum {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  PAUSED = "paused",
  DROPPED = "dropped",
}

// Base Course (matches Pydantic CourseBase)
export interface CourseBase {
  course_name: string;
  course_description?: string | null;
  instructor_name?: string | null;
  category?: string | null;
  start_date?: string | null; // ISO date (YYYY-MM-DD)
  end_date?: string | null;   // ISO date (YYYY-MM-DD)
}

// Course Create (matches Pydantic CourseCreate)
export interface CourseCreate extends CourseBase {}

// Course Update (matches Pydantic CourseUpdate)
export interface CourseUpdate extends Partial<CourseBase> {}

// Course (matches Pydantic Course for API responses)
export interface Course extends CourseBase {
  course_id: number;
  created_at: string;
  updated_at: string;
}

// Base UserCourseEnrollment (matches Pydantic UserCourseEnrollmentBase)
export interface UserCourseEnrollmentBase {
  user_id: number;
  course_id: number;
  progress_percentage?: number;
  status?: CourseStatusEnum;
}

// UserCourseEnrollment Create (matches Pydantic UserCourseEnrollmentCreate)
export interface UserCourseEnrollmentCreate extends UserCourseEnrollmentBase {}

// UserCourseEnrollment Update (matches Pydantic UserCourseEnrollmentUpdate)
export interface UserCourseEnrollmentUpdate {
  progress_percentage?: number | null;
  status?: CourseStatusEnum | null;
}

// UserCourseEnrollment (matches Pydantic UserCourseEnrollment for API responses)
export interface UserCourseEnrollment extends UserCourseEnrollmentBase {
  enrollment_id: number;
  enrollment_date: string;
  completed_date?: string | null;
  last_accessed_at?: string | null;
}

// Message Sender Role Enum (matches SQLAlchemy Enum)
export enum MessageSenderRoleEnum {
  USER = "user",
  ASSISTANT = "assistant",
  SYSTEM = "system",
}

// Base ChatMessage (matches Pydantic ChatMessageBase)
export interface ChatMessageBase {
  conversation_id: number;
  sender_role: MessageSenderRoleEnum;
  message_content: string;
  metadata?: any | null; // Corresponds to JSONB
}

// ChatMessage Create (matches Pydantic ChatMessageCreate)
export interface ChatMessageCreate extends ChatMessageBase {}

// ChatMessage Update (matches Pydantic ChatMessageUpdate)
export interface ChatMessageUpdate {
  message_content?: string | null;
  metadata?: any | null;
}

// ChatMessage (matches Pydantic ChatMessage for API responses)
export interface ChatMessage extends ChatMessageBase {
  message_id: number; // BIGSERIAL as number
  timestamp: string;
}

// Base Conversation (matches Pydantic ConversationBase)
export interface ConversationBase {
  user_id: number;
  title?: string | null;
}

// Conversation Create (matches Pydantic ConversationCreate)
export interface ConversationCreate extends ConversationBase {}

// Conversation Update (matches Pydantic ConversationUpdate)
export interface ConversationUpdate {
  title?: string | null;
}

// Conversation (matches Pydantic Conversation for API responses)
export interface Conversation extends ConversationBase {
  conversation_id: number;
  public_id: string;
  main_thread_id?: string | null;
  created_at: string;
  last_message_at?: string | null;
  messages?: ChatMessage[]; // Assuming messages are included
}

// ---------------- Branching Chat Types ----------------
export type ThreadKind = 'main' | 'subchat';
export type MessageRole = 'user' | 'assistant' | 'system' | 'tool';

export interface Thread {
  id: string;
  conversation_id: number;
  kind: ThreadKind;
  anchor_message_id?: string | null;
  anchor_revision_id?: string | null;
  active_leaf_message_id?: string | null;
  created_at: string; // ISO timestamp
}

export interface ThreadViewMessage {
  id: string;
  role: MessageRole;
  content: any; // { text, parts, toolCalls, metadata }
  path: string; // materialized path, e.g., m1.m1
}

export interface ThreadView {
  thread_id: string;
  messages: ThreadViewMessage[];
}

export interface AppendMessageRequest {
  threadId: string;
  parentMessageId?: string | null;
  role: MessageRole;
  content: any;
  idempotencyKey?: string | null;
}

export interface AppendMessageResponse {
  messageId: string;
  revisionId: string;
}

export interface EditMessageRequest {
  content: any;
  lastKnownRevisionId?: string | null;
}

// Branching chat: thread creation and revision switching
export interface CreateThreadRequest {
  conversationRef: string | number;
  kind?: ThreadKind; // default main
  anchorMessageId?: string | null;
  anchorRevisionId?: string | null;
}

export interface CreateThreadResponse {
  thread: Thread;
}

export type SwitchRevisionStrategy = 'deepest_compatible' | 'node';

export interface SwitchRevisionRequest {
  targetRevisionId: string;
  strategy?: SwitchRevisionStrategy; // default 'node'
  lastKnownRevisionId?: string | null;
}

// Base ActivityLog (matches Pydantic ActivityLogBase)
export interface ActivityLogBase {
  activity_type: string;
  user_id?: number | null;
  description?: string | null;
  details?: any | null; // Corresponds to JSONB
  ip_address?: string | null;
  user_agent?: string | null;
}

// ActivityLog Create (matches Pydantic ActivityLogCreate)
export interface ActivityLogCreate extends ActivityLogBase {}

// ActivityLog (matches Pydantic ActivityLog for API responses)
export interface ActivityLog extends ActivityLogBase {
  log_id: number; // BIGSERIAL as number
  timestamp: string;
}

// Token (matches Pydantic Token)
export interface Token {
  access_token: string;
  token_type: string;
  user_id: number;
  username: string;
  profile_exists?: boolean;
}

// TokenPayload / TokenData (matches Pydantic TokenData)
export interface TokenPayload {
  username?: string | null;
  // user_id?: number | null; // if you add it to backend TokenData
  // roles?: string[]; // if you add it to backend TokenData
}

// Add new interfaces for Profile data

export interface ProfileData {
  first_name: string;
  last_name: string;
  birthday: string; // Date will be string in YYYY-MM-DD format for JSON
  occupation?: string;
  school_organization?: string;
  favorite_subject_and_reasoning?: string;
  study_habits_methods?: string;
  challenging_subjects_areas?: string;
}

export interface ProfileMinimalResponse {
  user_id: number;
  message: string;
  first_name?: string;
  last_name?: string;
  profile_created: boolean;
}
